import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack } from "@mui/material";
import { tokens } from "../../theme";

/**
 * Small multi-series line chart for percentage trends.
 *
 * Both series share one axis on purpose — they are the same unit, so a second
 * y-scale would invent a relationship that isn't in the data.
 *
 * A `null` value is a gap, not a zero: a day the system recorded nothing is not
 * a day when nobody showed up, and drawing it at 0 would assert exactly that.
 * The path breaks instead.
 */

const PAD = { top: 14, right: 44, bottom: 22, left: 40 };
const HIT_RADIUS = 24;

const TrendChart = ({
  points = [],            // [{ x: "2026-08-01", label: "1 Aug" }]
  series = [],            // [{ key, label, color, values: (number|null)[] }]
  height = 180,
  yMin = 0,
  yMax = 100,
  formatValue = (v) => `${Math.round(v)}%`,
  markIndex = null,        // the day the rest of the page is showing
  markLabel = "selected",
}) => {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(640);
  const [hoverIndex, setHoverIndex] = useState(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWidth(Math.max(280, Math.floor(w)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const plotW = Math.max(1, width - PAD.left - PAD.right);
  const plotH = Math.max(1, height - PAD.top - PAD.bottom);

  const xAt = useCallback(
    (i) => (points.length <= 1 ? PAD.left : PAD.left + (i / (points.length - 1)) * plotW),
    [points.length, plotW]
  );
  // a baseline of zero is right for a percentage and wrong for a clock time —
  // 0-1440 would flatten every arrival into one line at the top
  const yAt = useCallback(
    (v) => {
      const span = Math.max(1e-9, yMax - yMin);
      const clamped = Math.max(yMin, Math.min(yMax, v));
      return PAD.top + plotH - ((clamped - yMin) / span) * plotH;
    },
    [plotH, yMax, yMin]
  );

  // a run of consecutive non-null values becomes one path; gaps split them
  const pathsFor = useCallback(
    (values) => {
      const runs = [];
      let run = [];
      values.forEach((v, i) => {
        if (v === null || v === undefined) {
          if (run.length) runs.push(run);
          run = [];
        } else {
          run.push([xAt(i), yAt(v)]);
        }
      });
      if (run.length) runs.push(run);
      return runs.map((r) =>
        r.length === 1
          ? null // a lone point can't be a line; the dot below carries it
          : r.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ")
      );
    },
    [xAt, yAt]
  );

  // isolated readings still need a mark, or a one-day island vanishes
  const isolatedPoints = useCallback(
    (values) =>
      values
        .map((v, i) => ({ v, i }))
        .filter(
          ({ v, i }) =>
            v !== null &&
            v !== undefined &&
            (values[i - 1] === null || values[i - 1] === undefined) &&
            (values[i + 1] === null || values[i + 1] === undefined)
        ),
    []
  );

  const lastRealIndex = useCallback((values) => {
    for (let i = values.length - 1; i >= 0; i -= 1) {
      if (values[i] !== null && values[i] !== undefined) return i;
    }
    return -1;
  }, []);

  // four bands, rounded to a readable step — the axis carries the values that
  // aren't directly labelled, so the numbers have to be ones people can hold
  const ticks = useMemo(() => {
    const raw = (yMax - yMin) / 4;
    const mag = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 1))));
    const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) || mag * 10;
    const out = [];
    for (let v = Math.ceil(yMin / step) * step; v <= yMax + 1e-9; v += step) {
      out.push(Math.round(v * 100) / 100);
    }
    return out;
  }, [yMax, yMin]);

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x < PAD.left - HIT_RADIUS || x > width - PAD.right + HIT_RADIUS) {
      setHoverIndex(null);
      return;
    }
    // snap to the nearest x — the reader aims at a date, not at a 2px line
    const ratio = points.length <= 1 ? 0 : (x - PAD.left) / plotW;
    const idx = Math.round(ratio * (points.length - 1));
    setHoverIndex(Math.max(0, Math.min(points.length - 1, idx)));
  };

  if (!points.length) return null;

  const hovered = hoverIndex === null ? null : points[hoverIndex];
  const tooltipLeft = hoverIndex === null ? 0 : xAt(hoverIndex);
  const flip = tooltipLeft > width * 0.6;

  return (
    <Box ref={wrapRef} sx={{ position: "relative", width: "100%" }}>
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={`${series.map((s) => s.label).join(" and ")} over ${points.length} days`}
        onPointerMove={onMove}
        onPointerLeave={() => setHoverIndex(null)}
        style={{ display: "block", touchAction: "none" }}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={PAD.left}
              x2={width - PAD.right}
              y1={yAt(t)}
              y2={yAt(t)}
              stroke={tokens.border}
              strokeWidth="1"
            />
            <text
              x={PAD.left - 8}
              y={yAt(t) + 3}
              textAnchor="end"
              fontSize="9"
              fill={tokens.textMuted}
            >
              {formatValue(t)}
            </text>
          </g>
        ))}

        {/* anchor: the day the KPI row and roster are showing, so the two halves
            of the page are visibly the same date */}
        {markIndex !== null && markIndex >= 0 && (
          <g>
            <line
              x1={xAt(markIndex)}
              x2={xAt(markIndex)}
              y1={PAD.top}
              y2={PAD.top + plotH}
              stroke={tokens.text}
              strokeWidth="1"
              strokeDasharray="3 3"
              opacity="0.35"
            />
            <text
              x={xAt(markIndex)}
              y={PAD.top - 4}
              textAnchor="middle"
              fontSize="9"
              fill={tokens.textMuted}
            >
              {markLabel}
            </text>
          </g>
        )}

        {hoverIndex !== null && (
          <line
            x1={xAt(hoverIndex)}
            x2={xAt(hoverIndex)}
            y1={PAD.top}
            y2={PAD.top + plotH}
            stroke={tokens.textMuted}
            strokeWidth="1"
          />
        )}

        {series.map((s) => (
          <g key={s.key}>
            {pathsFor(s.values).map((d, i) =>
              d ? (
                <path
                  key={i}
                  d={d}
                  fill="none"
                  stroke={s.color}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ) : null
            )}
            {isolatedPoints(s.values).map(({ v, i }) => (
              <circle
                key={`iso-${i}`}
                cx={xAt(i)}
                cy={yAt(v)}
                r="3.5"
                fill={s.color}
                stroke={tokens.card}
                strokeWidth="2"
              />
            ))}
          </g>
        ))}

        {/* end marker + direct label: the amber series fails 3:1 against the
            surface, so a visible label is required relief, not decoration */}
        {series.map((s) => {
          const i = lastRealIndex(s.values);
          if (i < 0) return null;
          return (
            <g key={`end-${s.key}`}>
              <circle
                cx={xAt(i)}
                cy={yAt(s.values[i])}
                r="4"
                fill={s.color}
                stroke={tokens.card}
                strokeWidth="2"
              />
              <text
                x={Math.min(xAt(i) + 8, width - 4)}
                y={yAt(s.values[i]) + 3}
                fontSize="10"
                fontWeight="700"
                fill={tokens.text}
              >
                {formatValue(s.values[i])}
              </text>
            </g>
          );
        })}

        {points.length > 1 && (
          <>
            <text x={PAD.left} y={height - 6} fontSize="9" fill={tokens.textMuted}>
              {points[0].label}
            </text>
            <text x={width - PAD.right} y={height - 6} textAnchor="end" fontSize="9" fill={tokens.textMuted}>
              {points[points.length - 1].label}
            </text>
          </>
        )}
      </svg>

      {hovered && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: flip ? "auto" : `${tooltipLeft + 10}px`,
            right: flip ? `${width - tooltipLeft + 10}px` : "auto",
            pointerEvents: "none",
            background: tokens.card,
            border: `1px solid ${tokens.border}`,
            borderRadius: "8px",
            boxShadow: "0 4px 12px -4px rgba(42,29,34,0.16)",
            p: "0.5rem 0.75rem",
            minWidth: "8rem",
            zIndex: 2,
          }}
        >
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: 0.5 }}>
            {hovered.label}
          </Box>
          {series.map((s) => {
            const v = s.values[hoverIndex];
            return (
              <Stack key={s.key} direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                <Box sx={{ width: "10px", height: "2px", background: s.color, borderRadius: "6px" }} />
                <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text }}>
                  {v === null || v === undefined ? "no data" : formatValue(v)}
                </Box>
                <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{s.label}</Box>
              </Stack>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default TrendChart;
